import { useCallback, useEffect } from "react";
import { useSpeechRecognitionManager } from "@/context/SpeechRecognitionContext"; //
import { useTimeout } from "../useTimeout";

interface SpeechToTextOptions {
  id: string;
  onTranscriptEnd: (finalTranscript: string) => void;
  lang: string;
  timeout?: number;
}

interface SpeechToTextResult {
  transcript: string;
  isListening: boolean;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
  browserSupportsVoice: boolean;
}

export const useSpeechToText = ({
  id,
  onTranscriptEnd,
  lang,
  timeout = 5000,
}: SpeechToTextOptions): SpeechToTextResult => {
  const {
    transcript: globalTranscript,
    isListening: globalIsListening,
    startListening: globalStartListening,
    stopListening: globalStopListening,
    resetTranscript: globalResetTranscript,
    browserSupportsSpeechRecognition,
    activeListenerId,
    setActiveListenerId,
  } = useSpeechRecognitionManager();

  const isThisInstanceListening = globalIsListening && activeListenerId === id;

  const timeoutDelay =
    isThisInstanceListening && !globalTranscript ? timeout : null;

  useTimeout(globalStopListening, timeoutDelay);

  useEffect(() => {
    if (!globalIsListening && globalTranscript && activeListenerId === id) {
      onTranscriptEnd(globalTranscript);
      globalResetTranscript();
      setActiveListenerId(null);
    }
  }, [
    globalIsListening,
    globalTranscript,
    onTranscriptEnd,
    globalResetTranscript,
    id,
    activeListenerId,
    setActiveListenerId,
  ]);

  const startListening = useCallback(async () => {
    setActiveListenerId(id);
    await globalStartListening(lang);
  }, [id, lang, setActiveListenerId, globalStartListening]);

  const stopListening = useCallback(() => {
    globalStopListening();
  }, [globalStopListening]);

  const resetTranscript = useCallback(() => {
    if (isThisInstanceListening) {
      globalResetTranscript();
    }
  }, [isThisInstanceListening, globalResetTranscript]);

  return {
    transcript: isThisInstanceListening ? globalTranscript : "",
    isListening: isThisInstanceListening,
    startListening,
    stopListening,
    resetTranscript,
    browserSupportsVoice: browserSupportsSpeechRecognition,
  };
};
